import { useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { ExternalLink, Monitor, RefreshCw, Smartphone } from "lucide-react";
import type { ShopStorefrontType } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useShopEventsQuery } from "@/features/shop-events/api";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Tab = "page" | "embed";
export type Device = "desktop" | "mobile";

/**
 * Mağaza önizleme paneli.
 *
 * - Public sayfa (Site/Link/Embed hepsi için) → shop URL'i iframe içinde.
 * - Embed widget (sadece Embed tipi) → tn-loader ile gömülü kart önizlemesi.
 *
 * Live editing senkronu YOK (form değişikliği DB'ye gitmeden iframe görmez);
 * organizer Save'den sonra "Yenile" basar veya iframe `key={shop.updatedAt}`
 * ile otomatik refresh olur.
 */
export function ShopPreviewCard({
  shopSlug,
  storefrontType,
  updatedAt,
}: {
  shopSlug: string;
  storefrontType: ShopStorefrontType;
  updatedAt: string;
}) {
  const t = useT("shops.preview");
  const eventsQuery = useShopEventsQuery(shopSlug);
  const visibleEvents = useMemo(
    () => (eventsQuery.data ?? []).filter((e) => e.status === "Listed"),
    [eventsQuery.data],
  );

  const [tab, setTab] = useState<Tab>("page");
  const [device, setDevice] = useState<Device>("desktop");
  const [reloadKey, setReloadKey] = useState(0);

  const shopBaseUrl =
    import.meta.env.VITE_SHOP_BASE_URL ?? "http://localhost:3002";

  const showEmbedTab = storefrontType === "Embed";
  const activeTab: Tab = showEmbedTab ? tab : "page";

  const pageUrl = `${shopBaseUrl}/${shopSlug}`;
  // Tüm etkinlikler embed'i — tek etkinlik kodunun önizlemesi etkinlik sayfasında.
  const embedUrl =
    visibleEvents.length > 0 ? `${shopBaseUrl}/embed/${shopSlug}` : null;

  const targetUrl = activeTab === "embed" ? embedUrl : pageUrl;
  // Shop bu parametreyle önizleme bandı gösterir ve satın almayı kapatır;
  // "yeni sekmede aç" linki gerçek sayfayı açar.
  const iframeSrc = targetUrl ? `${targetUrl}?tn_preview=1` : "about:blank";
  const iframeHeight = device === "mobile" ? 720 : 600;
  const iframeWidth = device === "mobile" ? 390 : "100%";
  // updatedAt + reloadKey + tab + device kombinasyonu iframe'i tamamen yeniden mount eder
  // (sadece src değişince Chrome history sayar; key ile temiz refresh).
  const iframeKey = `${updatedAt}-${reloadKey}-${activeTab}-${device}`;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">{t("title")}</CardTitle>
            <CardDescription>{t("description")}</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DeviceToggle value={device} onChange={setDevice} />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setReloadKey((k) => k + 1)}
              title={t("reload")}
              aria-label={t("reload")}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {targetUrl ? (
              <Button type="button" size="sm" variant="ghost" asChild>
                <a href={targetUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="me-1.5 h-4 w-4" />
                  {t("newTab")}
                </a>
              </Button>
            ) : null}
          </div>
        </div>
        {showEmbedTab ? (
          <div className="mt-3 inline-flex rounded-md border bg-muted/40 p-0.5">
            <TabButton
              active={activeTab === "page"}
              onClick={() => setTab("page")}
            >
              {t("tabPage")}
            </TabButton>
            <TabButton
              active={activeTab === "embed"}
              onClick={() => setTab("embed")}
            >
              {t("tabEmbed")}
            </TabButton>
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center rounded-md border bg-muted/30 p-3">
          {activeTab === "embed" && !embedUrl ? (
            <p className="px-6 py-12 text-center text-sm text-muted-foreground">
              <Trans
                i18nKey="shops.preview.embedEmpty"
                components={{ b: <span className="font-medium" /> }}
              />
            </p>
          ) : (
            <iframe
              key={iframeKey}
              src={iframeSrc}
              title={t("iframeTitle")}
              className={cn(
                "rounded-md border bg-background shadow-sm",
                device === "desktop" && "w-full",
              )}
              style={{
                width: iframeWidth,
                height: iframeHeight,
                maxWidth: "100%",
              }}
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          )}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          {t("hint")}
        </p>
      </CardContent>
    </Card>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded px-3 py-1 text-xs font-medium transition",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

export function DeviceToggle({
  value,
  onChange,
}: {
  value: Device;
  onChange: (v: Device) => void;
}) {
  const t = useT("shops.preview");
  return (
    <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
      <button
        type="button"
        onClick={() => onChange("desktop")}
        className={cn(
          "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
          value === "desktop"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
        title={t("desktop")}
        aria-label={t("desktop")}
        aria-pressed={value === "desktop"}
      >
        <Monitor className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange("mobile")}
        className={cn(
          "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
          value === "mobile"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
        title={t("mobile")}
        aria-label={t("mobile")}
        aria-pressed={value === "mobile"}
      >
        <Smartphone className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
